// Simplified WebSocket Context - Replaces complex per-task WebSocket connections
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys, invalidateQueries } from '../lib/query-client';
import { useAuth } from './AuthContext';
import { useApp } from './AppContext';
import { getQueryDebouncer } from '../utils/queryDebounce';

const WebSocketContext = createContext();

// WebSocket connection manager
class WebSocketManager {
  constructor() {
    this.ws = null;
    this.subscribers = new Map();
    this.messageSubscribers = new Map(); // Separate map for message-only subscribers
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000; // Start with 1 second
    this.isConnecting = false;
    this.shouldReconnect = true;
  }

  connect(url, token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      return Promise.resolve();
    }

    if (this.isConnecting) {
      return Promise.resolve();
    }

    this.isConnecting = true;
    this.shouldReconnect = true;

    return new Promise((resolve, reject) => {
      try {
        // Add auth token to WebSocket URL
        const wsUrl = new URL(url);
        wsUrl.searchParams.set('token', token);
        
        this.ws = new WebSocket(wsUrl.toString());

        this.ws.onopen = () => {
          this.isConnecting = false;
          this.reconnectAttempts = 0;
          this.reconnectDelay = 1000;
          this.notifySubscribers('connected', { connected: true });
          resolve();
        };

        this.ws.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            console.log('[WebSocket] Received message:', data);
            this.notifySubscribers('message', data);
            // Also notify message-only subscribers directly with parsed data
            this.notifyMessageSubscribers(data);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };

        this.ws.onclose = (event) => {
          this.isConnecting = false;
          this.notifySubscribers('disconnected', { connected: false });
          
          if (this.shouldReconnect && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect(url, token);
          }
        };

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error);
          this.isConnecting = false;
          this.notifySubscribers('error', error);
          reject(error);
        };
      } catch (error) {
        this.isConnecting = false;
        reject(error);
      }
    });
  }

  scheduleReconnect(url, token) {
    this.reconnectAttempts++;
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000);
    
    
    setTimeout(() => {
      if (this.shouldReconnect) {
        this.connect(url, token).catch(error => {
          console.error('Reconnect failed:', error);
        });
      }
    }, delay);
  }

  disconnect() {
    this.shouldReconnect = false;
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    this.subscribers.clear();
    this.messageSubscribers.clear();
  }

  subscribe(id, callback) {
    this.subscribers.set(id, callback);

    // Return unsubscribe function
    return () => {
      this.subscribers.delete(id);
    };
  }

  // Subscribe to receive only message events (parsed JSON data)
  subscribeToMessages(id, callback) {
    console.log('[WebSocketManager] Adding message subscriber:', id);
    this.messageSubscribers.set(id, callback);

    // Return unsubscribe function
    return () => {
      console.log('[WebSocketManager] Removing message subscriber:', id);
      this.messageSubscribers.delete(id);
    };
  }

  notifySubscribers(type, data) {
    this.subscribers.forEach(callback => {
      try {
        callback(type, data);
      } catch (error) {
        console.error('Error in WebSocket subscriber:', error);
      }
    });
  }

  notifyMessageSubscribers(data) {
    console.log('[WebSocketManager] Notifying', this.messageSubscribers.size, 'message subscribers');
    this.messageSubscribers.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error('Error in WebSocket message subscriber:', error);
      }
    });
  }

  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  send(data) {
    if (this.isConnected()) {
      this.ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }
}

// Create singleton instance
const wsManager = new WebSocketManager();

export const WebSocketProvider = ({ children }) => {
  const [connectionState, setConnectionState] = useState({
    connected: false,
    connecting: false,
    error: null,
  });
  
  const { user, isAuthenticated } = useAuth();
  const { showError, showSuccess } = useApp();
  const queryClient = useQueryClient();
  const subscriptionRef = useRef(null);
  const debouncerRef = useRef(null);
  const shouldConnectRef = useRef(false);
  const connectionStateRef = useRef({ connected: false, connecting: false });

  // WebSocket URL construction
  const getWebSocketUrl = () => {
    // Use explicitly configured WebSocket URL if available (set in docker-compose)
    const wsUrl = process.env.REACT_APP_WS_URL;
    if (wsUrl) {
      return `${wsUrl}/ws/global`;
    }
    // Fallback: derive from window.location
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const host = window.location.hostname;
    const port = window.location.port || (window.location.protocol === 'https:' ? '443' : '80');
    return `${protocol}//${host}${port !== '80' && port !== '443' ? ':' + port : ''}/ws/global`;
  };

  // Handle WebSocket messages
  const handleWebSocketMessage = (type, data) => {
    switch (type) {
      case 'connected':
        setConnectionState({ connected: true, connecting: false, error: null });
        break;

      case 'disconnected':
        setConnectionState({ connected: false, connecting: false, error: null });
        break;

      case 'error':
        setConnectionState({ connected: false, connecting: false, error: data });
        // Don't show error toast - the Live indicator shows connection status
        break;

      case 'message':
        handleTaskMessage(data);
        break;

      default:
        // Unknown message type - log and ignore
        console.debug('[WebSocket] Unknown message type:', type);
        break;
    }
  };

  // Handle task-related messages with debouncing
  const handleTaskMessage = (data) => {
    console.log('[WebSocket] handleTaskMessage called with:', data);
    if (!data.type) return;

    // Initialize debouncer if needed
    if (!debouncerRef.current) {
      debouncerRef.current = getQueryDebouncer(queryClient, 500);
    }
    const debouncer = debouncerRef.current;

    switch (data.type) {
      case 'task.created':
      case 'task.updated':
      case 'plan.status_changed':
      case 'plan.completed':
        console.log('[WebSocket] Event:', data.type, data.task_id || data.plan_id);
        // Debounce list/stats invalidations - these are expensive and can be batched
        debouncer.invalidate('tasks.list', () => {
          invalidateQueries.tasks.list();
        });
        debouncer.invalidate('tasks.stats', () => {
          invalidateQueries.tasks.stats();
        });

        // Debounce task-specific invalidations by ID
        // During decomposition, we get many events for the same project
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            console.log(`[WebSocket] Invalidating task detail: ${data.task_id}`);
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            console.log(`[WebSocket] Invalidating task tree: ${data.task_id}`);
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }

        // Debounce parent task invalidations
        if (data.parent_id) {
          debouncer.invalidate(`task.detail.${data.parent_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.parent_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.parent_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.parent_id)
            });
          });
        }

        // Debounce plan-level invalidations
        if (data.plan_id && data.plan_id !== data.task_id) {
          debouncer.invalidate(`task.detail.${data.plan_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.plan_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.plan_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.plan_id)
            });
          });
        }

        if (data.plan_id) {
          debouncer.invalidate(`task.detail.${data.plan_id}`, () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tasks.detail(data.plan_id) });
          });
          debouncer.invalidate(`task.tree.${data.plan_id}`, () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.tasks.tree(data.plan_id) });
          });
        }
        break;
        
      case 'decomposition.started':
      case 'decomposition.completed':
      case 'decomposition.error':
        // These events indicate changes to the task tree structure
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }
        break;

      case 'task.approved':
        // Show success notification
        showSuccess(`Task approved: ${data.task_id} by ${data.approved_by}`);

        // Invalidate task list and stats to refresh pending approvals (debounced)
        debouncer.invalidate('tasks.list', () => invalidateQueries.tasks.list());
        debouncer.invalidate('tasks.stats', () => invalidateQueries.tasks.stats());

        // Update specific task (debounced)
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }
        break;

      case 'task.rejected':
        // Show rejection notification
        const message = data.new_description
          ? `Task rejected and resubmitted: ${data.task_id} by ${data.rejected_by}`
          : `Task rejected: ${data.task_id} by ${data.rejected_by}`;
        showError(message);

        // Invalidate task list and stats to refresh pending approvals (debounced)
        debouncer.invalidate('tasks.list', () => invalidateQueries.tasks.list());
        debouncer.invalidate('tasks.stats', () => invalidateQueries.tasks.stats());

        // Update specific task (debounced)
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }
        break;

      case 'task.completed':
        // Invalidate related queries (debounced)
        debouncer.invalidate('tasks.list', () => invalidateQueries.tasks.list());
        debouncer.invalidate('tasks.stats', () => invalidateQueries.tasks.stats());

        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }
        break;
        
      case 'task.deleted':
        // Remove from cache immediately for the specific task
        if (data.task_id) {
          queryClient.removeQueries({ 
            queryKey: queryKeys.tasks.detail(data.task_id) 
          });
          queryClient.removeQueries({ 
            queryKey: queryKeys.tasks.tree(data.task_id) 
          });
        }
        
        // Debounce list and stats invalidations to prevent flooding
        if (!debouncerRef.current) {
          debouncerRef.current = getQueryDebouncer(queryClient, 500);
        }
        
        debouncerRef.current.invalidate('tasks.list', () => {
          invalidateQueries.tasks.list();
        });
        
        debouncerRef.current.invalidate('tasks.stats', () => {
          invalidateQueries.tasks.stats();
        });
        break;
        
      case 'operator.status':
        // Handle operator status updates
        // Could show loading states based on operator progress
        break;
        
      case 'service.status':
        // Handle detailed service status updates
        // Don't show notifications - let individual components handle service.status events
        // This allows DecompositionStatus to display them as status messages instead

        // Debounce query invalidation - service.status events can be very frequent
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }
        break;

      case 'event_modeling.started':
      case 'event_modeling.progress':
        // Let EventModelTab handle these via subscribe - don't show notifications
        break;

      case 'event_modeling.completed':
        // Show success notification
        showSuccess(data.message || 'Event model generated successfully');

        // Invalidate task queries to refresh the event model data (debounced)
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
          debouncer.invalidate(`task.tree.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.tree(data.task_id)
            });
          });
        }
        break;

      case 'event_modeling.error':
        // Show error notification
        showError(data.message || 'Event model generation failed');
        break;

      case 'questions.ready':
        // Clarifying questions are ready - invalidate questions query immediately
        console.log('[WebSocket] Questions ready event, invalidating questions query');
        if (data.task_id) {
          // Use immediate invalidation, not debounced, so questions appear quickly
          queryClient.invalidateQueries({
            queryKey: ['tasks', 'questions', data.task_id]
          });
          // Also invalidate the main task detail since questions are stored in context
          queryClient.invalidateQueries({
            queryKey: queryKeys.tasks.detail(data.task_id)
          });
        }
        break;

      case 'context.updated':
        // Context was updated (features_and_stories, decomposed_tasks, etc.)
        console.log('[WebSocket] Context updated:', data.data_key);
        if (data.task_id) {
          debouncer.invalidate(`task.detail.${data.task_id}`, () => {
            queryClient.invalidateQueries({
              queryKey: queryKeys.tasks.detail(data.task_id)
            });
          });
        }
        break;

      default:
        // Unknown task event - log for debugging
        console.debug('[WebSocket] Unknown task event:', data.type);
        break;
    }
  };

  // Helper to update both state and ref
  const updateConnectionState = React.useCallback((newState) => {
    connectionStateRef.current = {
      connected: newState.connected,
      connecting: newState.connecting
    };
    setConnectionState(newState);
  }, []);

  // Manual connection control - only connect when explicitly requested
  // Uses refs for state checking to avoid dependency loops
  const connect = React.useCallback(() => {
    if (!isAuthenticated() || !user) {
      console.log('Cannot connect WebSocket: not authenticated');
      return Promise.reject(new Error('Not authenticated'));
    }

    // Check ref instead of state to avoid dependency loops
    if (connectionStateRef.current.connected || connectionStateRef.current.connecting) {
      console.log('WebSocket already connected or connecting');
      return Promise.resolve();
    }

    const token = localStorage.getItem('token') || 'dev-bypass-token';
    updateConnectionState({ connected: false, connecting: true, error: null });

    // Subscribe to WebSocket events
    subscriptionRef.current = wsManager.subscribe(
      'app-context',
      handleWebSocketMessage
    );

    // Connect
    return wsManager.connect(getWebSocketUrl(), token)
      .then(() => {
        updateConnectionState({ connected: true, connecting: false, error: null });
        shouldConnectRef.current = true;
      })
      .catch(error => {
        console.error('WebSocket connection failed:', error);
        updateConnectionState({ connected: false, connecting: false, error });
        if (subscriptionRef.current) {
          subscriptionRef.current();
          subscriptionRef.current = null;
        }
        throw error;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user, updateConnectionState]);

  const disconnect = React.useCallback(() => {
    if (subscriptionRef.current) {
      subscriptionRef.current();
      subscriptionRef.current = null;
    }

    // Flush any pending debounced invalidations
    if (debouncerRef.current) {
      debouncerRef.current.flush();
    }

    wsManager.disconnect();
    updateConnectionState({ connected: false, connecting: false, error: null });
    shouldConnectRef.current = false;
  }, [updateConnectionState]);

  // Only disconnect when user logs out (don't auto-connect)
  useEffect(() => {
    if (!isAuthenticated() && shouldConnectRef.current) {
      disconnect();
    }

    // Cleanup on unmount
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current();
      }
      if (debouncerRef.current) {
        debouncerRef.current.cancel();
      }
    };
  }, [isAuthenticated, disconnect]);

  // Context value
  const contextValue = {
    // Connection state
    ...connectionState,
    
    // Actions
    connect,
    disconnect,
    send: (data) => wsManager.send(data),
    reconnect: () => {
      if (isAuthenticated() && user) {
        const token = localStorage.getItem('token') || 'dev-bypass-token';
        return wsManager.connect(getWebSocketUrl(), token);
      }
    },
    
    // Subscribe to receive WebSocket messages (used by LiveActivityFeed, etc.)
    subscribe: (callback) => {
      const id = `subscriber-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      return wsManager.subscribeToMessages(id, callback);
    },
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
};

// Custom hook to use WebSocket context
export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export default WebSocketContext;